import type { Token, TokenType } from "./tokenizer";

// AST node syntax is adapted from the ESTree spec:
// https://github.com/estree/estree
// https://hexdocs.pm/estree/ESTree.html
export type ASTNode =
  | { type: "Literal"; value: number }
  | { type: "Identifier"; name: string }
  | {
      type: "BinaryExpression";
      operator: string;
      left: ASTNode;
      right: ASTNode;
    }
  | { type: "UnaryExpression"; operator: "-"; argument: ASTNode }
  | { type: "CallExpression"; callee: string; arguments: ASTNode[] };

export class Parser {
  private tokens: Token[];
  private pos: number;

  private constructor(tokens: Token[]) {
    this.tokens = tokens;
    this.pos = 0;
  }

  static parse(tokens: Token[]): ASTNode {
    const parser = new Parser(tokens);
    const ast = parser.parseAdditive();
    if (parser.currentToken.type !== "EOF") {
      throw new Error(
        `Unexpected token '${parser.currentToken.value}' at position ${parser.currentToken.position}`,
      );
    }
    return ast;
  }

  private get currentToken(): Token {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    return this.tokens[this.pos++];
  }

  // Level 1: lowest precedence — addition and subtraction
  private parseAdditive(): ASTNode {
    let left = this.parseMultiplicative();
    while (this.currentToken.value === "+" || this.currentToken.value === "-") {
      const op = this.currentToken.value;
      this.consume();
      const right = this.parseMultiplicative();
      left = { type: "BinaryExpression", operator: op, left, right };
    }
    return left;
  }

  // Level 2: multiplication, division, modulo
  private parseMultiplicative(): ASTNode {
    let left = this.parsePower();
    while (
      this.currentToken.value === "*" ||
      this.currentToken.value === "/" ||
      this.currentToken.value === "%"
    ) {
      const op = this.currentToken.value;
      this.consume();
      const right = this.parsePower();
      left = { type: "BinaryExpression", operator: op, left, right };
    }
    return left;
  }

  // Level 3: exponentiation (right-associative)
  private parsePower(): ASTNode {
    const base = this.parseUnary();
    if (this.currentToken.value === "^") {
      const op = this.currentToken.value;
      this.consume();
      const exponent = this.parsePower(); // right-recursive
      return {
        type: "BinaryExpression",
        operator: op,
        left: base,
        right: exponent,
      };
    }
    return base;
  }

  // Level 4: unary minus
  private parseUnary(): ASTNode {
    if (this.currentToken.value === "-") {
      this.consume();
      const argument = this.parseUnary();
      return { type: "UnaryExpression", operator: "-", argument };
    }
    return this.parsePrimary();
  }

  // Level 5: literals, identifiers, function calls, parenthesized expressions
  private parsePrimary(): ASTNode {
    const token = this.currentToken;

    if (token.type === "NUMBER") {
      this.consume();
      return { type: "Literal", value: token.value as number };
    }

    if (token.type === "IDENTIFIER") {
      const name = token.value as string;
      this.consume();
      // Function call — capture the type in a fresh local variable after each
      // consume() so TypeScript's narrowing from the outer check doesn't persist.
      if (this.currentToken.type === "PAREN_OPEN") {
        this.consume(); // consume '('
        const args: ASTNode[] = [];
        // Cast to the full TokenType union to prevent TypeScript from
        // narrowing this variable based on the outer PAREN_OPEN check.
        let peekType = this.currentToken.type as TokenType;
        if (peekType !== "PAREN_CLOSE") {
          args.push(this.parseAdditive());
          peekType = this.currentToken.type as TokenType;
          while (peekType === "COMMA") {
            this.consume(); // consume ','
            args.push(this.parseAdditive());
            peekType = this.currentToken.type as TokenType;
          }
        }
        peekType = this.currentToken.type as TokenType;
        if (peekType !== "PAREN_CLOSE") {
          throw new Error(
            `Expected ')' at position ${this.currentToken.position}, got '${this.currentToken.value}'`,
          );
        }
        this.consume(); // consume ')'
        return { type: "CallExpression", callee: name, arguments: args };
      }
      return { type: "Identifier", name };
    }

    if (token.type === "PAREN_OPEN") {
      this.consume(); // consume '('
      const expr = this.parseAdditive();
      if (this.currentToken.type !== "PAREN_CLOSE") {
        throw new Error(
          `Expected ')' at position ${this.currentToken.position}, got '${this.currentToken.value}'`,
        );
      }
      this.consume(); // consume ')'
      return expr;
    }

    throw new Error(
      `Unexpected token '${token.value}' (${token.type}) at position ${token.position}`,
    );
  }
}
